/**
 * Telas PÚBLICAS: projeto compartilhado por link, aceite de convite (plano e
 * projeto), consentimento OAuth do conector MCP e a landing do conector.
 *
 * Todas rodam fora do app autenticado, então o idioma vem do prefixo da URL
 * (/en, /es) ou da preferência salva. Ver src/i18n/locale.ts.
 *
 * Regra que vale em todo o namespace: o que o banco/protocolo persiste continua
 * sendo a CHAVE (view 'gantt'|'kanban'|'eap', modo de cor 'level'|'progress',
 * escopo OAuth 'mcp', cor de destaque em hexadecimal). Só o rótulo vem daqui.
 */
const sharing = {
  /** Casca da tela /share/:token — senha, cabeçalho, abas e rodapé. */
  publicProject: {
    loading: 'Carregando projeto...',
    loadingView: 'Carregando visualização...',
    notFound: 'Projeto não encontrado ou link expirado',
    unavailableTitle: 'Projeto não disponível',
    backHome: 'Voltar para a página inicial',

    protectedTitle: 'Projeto protegido',
    protectedHint: 'Este projeto requer uma senha para visualização',
    passwordPlaceholder: 'Digite a senha',
    submit: 'Acessar',
    tooManyAttempts: 'Muitas tentativas. Aguarde um momento.',
    wrongPassword: 'Senha incorreta',
    lockedOut: 'Muitas tentativas incorretas. Aguarde 15 minutos.',
    verifyFailed: 'Erro ao verificar senha',
    loadFailed: 'Erro ao carregar projeto',

    untitled: 'Projeto Compartilhado',
    taskCount: { one: '{{done}}/{{total}} tarefa', other: '{{done}}/{{total}} tarefas' },
    viewOnly: 'Visualização',
    sharedVia: 'Compartilhado via',

    /** Indexado pelo valor persistido em settings.publish.defaultView. */
    views: {
      gantt: 'Gantt',
      kanban: 'Kanban',
      eap: 'EAP',
    },
  },

  /** Gantt em modo somente leitura da tela compartilhada. */
  gantt: {
    emptyTitle: 'Nenhuma tarefa encontrada',
    emptyHint: 'Este projeto ainda não possui tarefas no cronograma.',

    /** Indexado pelo viewMode do UIStateContext. */
    viewMode: {
      days: 'Dias',
      weeks: 'Semanas',
      months: 'Meses',
    },

    columns: {
      index: '#',
      name: 'Nome da tarefa',
      description: 'Descrição',
      assigneeShort: 'Resp.',
      assignee: 'Responsável',
      start: 'Início',
      end: 'Término',
      progress: 'Progresso',
      status: 'Status',
    },
  },

  /** Kanban em modo somente leitura. */
  kanban: {
    noProject: 'Nenhum projeto encontrado',
    emptyColumn: 'Nenhuma tarefa',
    milestone: 'Marco',
    blocked: 'Bloqueado',
    dependencies: { one: '{{count}} dependência', other: '{{count}} dependências' },
  },

  /** EAP (WBS) em modo somente leitura. */
  eap: {
    empty: 'Nenhuma tarefa para exibir na EAP.',
    colorBy: 'Colorir por...',

    /** Indexado pelo discriminante do switch em SharedEapNode. */
    colorModes: {
      default: 'Cor padrão',
      level: 'Por nível',
      progress: 'Por progresso',
      priority: 'Por prioridade',
    },

    expandAll: 'Expandir todos',
    expand: 'Expandir',
    collapseAll: 'Recolher todos',
    collapse: 'Recolher',
    zoomOut: 'Diminuir zoom',
    zoomIn: 'Aumentar zoom',
    zoomFit: 'Ajustar zoom',
  },

  /** Menu de três pontinhos. Os rótulos das ações vêm de quem monta o menu. */
  actionMenu: {
    tooltip: 'Ações',
  },

  /** Menu de contexto de linha (botão direito) das tabelas de módulo. */
  contextMenu: {
    duplicate: 'Duplicar',
    highlightRow: 'Destacar linha',
    delete: 'Excluir',
    deleteSelected: 'Excluir {{count}} selecionados',

    /**
     * Indexado pela COR gravada na linha (HIGHLIGHT_COLORS.value), porque é ela
     * que vai para o banco. `none` é o valor null.
     */
    highlight: {
      none: 'Sem cor',
      '#fef3c7': 'Amarelo',
      '#fce7f3': 'Rosa',
      '#dbeafe': 'Azul',
      '#dcfce7': 'Verde',
      '#f3e8ff': 'Roxo',
      '#fed7aa': 'Laranja',
      '#e5e7eb': 'Cinza',
    },
  },

  /** /invite/:token — convite de PLANO enviado pelo admin. */
  planInvite: {
    loading: 'Carregando...',
    notFound: 'Convite não encontrado ou já utilizado.',
    expired: 'Este convite expirou.',
    wrongAccount: 'Este convite é destinado a outro email ({{email}}). Faça login com a conta correta.',
    acceptFailed: 'Erro ao aceitar convite.',

    acceptedTitle: 'Convite aceito!',
    acceptedBody: 'Seu plano foi atualizado. Redirecionando...',

    errorTitle: 'Ops!',
    goToApp: 'Ir para o app',

    title: 'Você foi convidado!',
    subtitle: 'Você recebeu um convite para usar o MeuGantt com o plano:',
    free: 'Grátis',
    pricePerMonth: '{{price}}/mês',
    inviteFor: 'Convite para:',
    expiresAt: 'Expira em: {{date}}',
    accepting: 'Aceitando...',
    accept: 'Aceitar Convite',
  },

  /** /project-invite/:token — convite para um PROJETO específico. */
  projectInvite: {
    loading: 'Carregando convite...',
    notFound: 'Convite não encontrado.',
    expired: 'Este convite expirou. Peça ao administrador para reenviar.',
    loadFailed: 'Erro ao carregar convite.',
    acceptFailed: 'Erro ao aceitar convite.',

    bannerTitle: 'Convite para projeto',
    /** Fica entre o nome de quem convidou e o nome do projeto, ambos em negrito. */
    invitedYouTo: 'convidou você para o projeto',
    loginHint: 'Faça login ou crie uma conta para aceitar o convite',

    accepting: 'Aceitando convite...',
    acceptedTitle: 'Convite aceito!',
    acceptedBefore: 'Você agora é membro do projeto',
    acceptedAfter: '. Redirecionando...',

    alreadyTitle: 'Você já é membro',
    alreadyBefore: 'Você já faz parte do projeto',
    alreadyAfter: '.',

    invalidTitle: 'Convite inválido',
    goToApp: 'Ir para o MeuGantt',
  },

  /** /oauth/consent — tela de autorização do conector MCP. */
  oauth: {
    invalidRequest: 'Requisição OAuth inválida — faltando parâmetros obrigatórios.',
    sessionExpired: 'Sua sessão expirou. Faça login novamente para conectar o MeuGantt.',
    callbackError: 'Erro {{status}} ao concluir autorização.',
    denied: 'Acesso negado. Você pode fechar essa janela.',
    noRedirect: 'Worker não retornou URL de redirecionamento.',

    invalidTitle: 'Requisição inválida',
    missingParams: 'Parâmetros OAuth ausentes. Volte ao aplicativo que iniciou a conexão.',

    title: 'Conectar ao MeuGantt',
    unknownClient: 'Aplicativo externo',
    /** Entre o nome do cliente e o email do usuário, ambos em negrito. */
    requestIntro: 'está pedindo permissão para acessar sua conta MeuGantt em nome de',
    requestOutro: '.',

    scopesTitle: 'Permissões solicitadas',
    /**
     * Indexado pelo ESCOPO do protocolo OAuth, que continua em inglês por ser
     * contrato: só a descrição ao lado é traduzida.
     */
    scopes: {
      mcp: 'Ler e modificar dados dos seus projetos, tarefas, sprints, riscos, EVM e equipe',
    },

    noticeBefore: 'O aplicativo só vê os dados que',
    noticeYou: 'você',
    noticeAfter: 'já tem acesso no MeuGantt — autenticação via OAuth 2.1, sem expor sua senha. Você pode revogar essa conexão a qualquer momento em',
    noticeSettingsPath: 'Configurações → Aplicativos conectados',
    noticeEnd: '.',

    learnMore: 'Saiba mais sobre o conector',
    deny: 'Recusar',
    allow: 'Permitir',
  },

  /** /integracoes/claude — landing pública do conector MCP. */
  mcp: {
    hero: {
      badge: 'Conector oficial para o Claude (Anthropic)',
      titleBefore: 'Seu MeuGantt direto no',
      titleHighlight: 'Claude',
      titleAfter: ': gerencie projetos por conversa',
      subtitle: 'Crie tarefas, atualize status, gere relatórios executivos, analise capacidade da equipe e investigue a saúde do portfólio — tudo conversando com o Claude. Mais de 80 ferramentas MCP cobrindo Gantt, Kanban, RACI, EVM, riscos, custos e governança PMBOK 8.',
      ctaPrimary: 'Conectar ao Claude',
      ctaSecondary: 'Ver casos de uso',
    },

    install: {
      eyebrow: 'Como conectar',
      title: '3 passos. Demora 2 minutos.',
      intro: 'O fluxo é OAuth 2.1 padrão. Você autoriza explicitamente no domínio meugantt.com.br e o Claude passa a operar usando suas mesmas credenciais (com RLS aplicado).',
      step1Title: 'No Claude (claude.ai ou Claude Desktop), abra Configurações → Conectores',
      step1Body: 'Procure por "MeuGantt" no diretório oficial. (Em breve listado oficialmente — enquanto isso, use a opção "Add Custom Connector".)',
      step2Title: 'Cole a URL do conector',
      step3Title: 'Autorize no MeuGantt',
      step3Body: 'Você é redirecionado para meugantt.com.br/oauth/consent. Faça login (se ainda não estiver) e clique em "Autorizar". Pronto — o Claude já tem acesso.',
    },

    useCases: {
      eyebrow: 'O que dá pra fazer',
      title: 'Conversa, e o Claude faz.',
      intro: 'Exemplos reais de como nossos usuários estão usando o conector no dia a dia. Você pode parafrasear, pedir variações, encadear ações — o Claude entende contexto e mantém o histórico do projeto.',
      /** O `desc` já vem com as aspas do original, para não mudar o que a tela mostra hoje. */
      items: [
        {
          title: 'Crie tarefas conversando',
          desc: '"Adiciona 5 tarefas no projeto Lançamento Q2: pesquisa, design, dev backend, dev frontend e go-live, com dependências em cascata e prazos a partir de segunda-feira."',
        },
        {
          title: 'Atualize status em massa',
          desc: '"Marca como concluídas todas as tarefas da fase de Discovery do projeto Pulsar e move pra coluna Done do Kanban."',
        },
        {
          title: 'Gere relatórios executivos',
          desc: '"Me dá o briefing executivo do portfólio: SPI, CPI, EAC e os 3 riscos mais críticos de cada projeto ativo."',
        },
        {
          title: 'Consulte capacidade da equipe',
          desc: '"Quem da equipe tá sobrecarregado essa semana? Sugere realocações pra distribuir a carga."',
        },
        {
          title: 'Investigue saúde de projeto',
          desc: '"O projeto Mercurius tá com SPI 0.85 — me explica o que tá atrasando, lista as tarefas críticas e sugere próximos passos."',
        },
        {
          title: 'Gerencie RACI e governança',
          desc: '"Define o João como Accountable e a Maria como Responsible nas tarefas de homologação do projeto Pulsar."',
        },
      ],
    },

    tools: {
      eyebrow: 'Ferramentas disponíveis',
      title: '81 ferramentas MCP. Cobre o produto inteiro.',
      intro: 'Cada ferramenta tem schema validado, descrição explícita e marcações de segurança (read-only ou destructive). O Claude sabe quando pode agir sozinho e quando precisa pedir sua confirmação.',
      /** Indexado pela chave da categoria em McpConnectorPage (ícone e contagem ficam no código). */
      categories: {
        tasks: { label: 'Tarefas e cronograma', examples: 'criar, atualizar, mover no Kanban, vincular a sprint' },
        projects: { label: 'Projetos e Kanban', examples: 'listar projetos, mover cards, gerenciar status' },
        portfolio: { label: 'Portfólio e EVM', examples: 'snapshot semanal, executive brief, saúde de projeto' },
        team: { label: 'Time e capacidade', examples: 'membros, alocação, heatmap, RACI' },
        risks: { label: 'Riscos, mudanças, custos', examples: 'risk register, change requests, controle de orçamento' },
        notifications: { label: 'Notificações e contexto', examples: 'listar, marcar lidas, enums, próximas ações sugeridas' },
      },
    },

    security: {
      eyebrow: 'Segurança e privacidade',
      title: 'OAuth 2.1, RLS e controle total nas suas mãos',
      items: [
        'Autorização OAuth 2.1 com PKCE — padrão de mercado, igual ao usado por GitHub e Google',
        'Mesmo Row Level Security (RLS) da plataforma web — o Claude vê só o que você vê',
        'Tokens criptografados em repouso na Cloudflare KV; rotacionáveis e revogáveis',
        'Toda ação destrutiva (excluir tarefa, remover membro...) exige confirmação explícita',
        'Transporte HTTPS/TLS obrigatório; logs de auditoria de eventos sensíveis por 90 dias',
        'LGPD: você pode revogar acesso a qualquer momento; tokens são invalidados imediatamente',
      ],
      privacyLink: '→ Política de Privacidade (seção MCP)',
      termsLink: '→ Termos de Uso',
    },

    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          q: 'O Claude consegue ver dados de outros usuários ou organizações?',
          a: 'Não. O conector respeita as mesmas regras de visibilidade (Row Level Security) da plataforma web. O Claude só acessa dados que você mesmo acessaria normalmente.',
        },
        {
          q: 'Como funciona a autorização?',
          a: 'Padrão OAuth 2.1 com PKCE. Na primeira conexão, você é levado ao MeuGantt para aprovar explicitamente. Sem essa aprovação, nenhum dado trafega.',
        },
        {
          q: 'Como revogo o acesso?',
          a: 'Em Configurações → Conectores no Claude, remova o MeuGantt. Os tokens são invalidados imediatamente. Alternativamente, envie um email para contato@meugantt.com.br pedindo invalidação manual.',
        },
        {
          q: 'O conector tá em qual plano? Tem custo extra?',
          a: 'O conector está disponível para todos os planos (Free, Starter, Professional) sem custo adicional. Os limites de uso do MeuGantt seguem o seu plano normalmente.',
        },
        {
          q: 'É seguro? Quem garante?',
          a: 'OAuth 2.1 + PKCE, tokens criptografados em repouso (Cloudflare KV), HTTPS obrigatório, todas as ações destrutivas exigem confirmação explícita, RLS aplicado a 100% das queries.',
        },
        {
          q: 'Onde reportar bug ou pedir feature?',
          a: 'Email contato@meugantt.com.br. Respondemos em até 24h úteis.',
        },
      ],
    },

    cta: {
      title: 'Pronto para gerenciar projetos por conversa?',
      subtitle: 'Free, Starter ou Professional — o conector funciona em todos os planos, sem custo adicional.',
      primary: 'Conectar agora',
      secondary: 'Voltar pro MeuGantt',
    },

    /** Texto do JSON-LD (SoftwareApplication) desta página. */
    schema: {
      description: 'Conector oficial MeuGantt para o assistente Claude, da Anthropic. Permite operar projetos, tarefas, Kanban, portfólio, EVM, riscos, custos e equipes diretamente por conversa.',
    },
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type sharingCopy = typeof sharing;

export default sharing;
